import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

import {
  addMcpServer,
  listMcpServers,
  type McpServer,
  removeMcpServer,
} from "../../../mcp/api";

export const Route = createFileRoute("/_workspace/settings/mcp")({
  component: ConnectionsPane,
});

/** State → small colored status chip. Only `ready` is usable by the agent. */
function StatusChip({ state }: { state: McpServer["state"] }) {
  if (state === "ready") {
    return (
      <span className="shrink-0 rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
        Ready
      </span>
    );
  }
  if (state === "authenticating") {
    return (
      <span className="shrink-0 rounded-full bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-600 dark:text-amber-400">
        Needs authorization
      </span>
    );
  }
  if (state === "failed") {
    return (
      <span className="shrink-0 rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive">
        Failed
      </span>
    );
  }
  return (
    <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
      {state}
    </span>
  );
}

function ConnectionsPane() {
  const qc = useQueryClient();
  const servers = useQuery({
    queryKey: ["mcp-servers"],
    queryFn: listMcpServers,
  });

  const [url, setUrl] = useState("");
  const [bearer, setBearer] = useState("");

  const add = useMutation({
    mutationFn: () =>
      addMcpServer({
        url: url.trim(),
        bearer: bearer.trim() || undefined,
      }),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ["mcp-servers"] });
      // OAuth server: send the owner to the provider's consent screen. The
      // provider redirects back to /api/mcp/callback → /settings/mcp.
      if ("authUrl" in result && result.authUrl) {
        window.location.assign(result.authUrl);
        return;
      }
      setUrl("");
      setBearer("");
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => removeMcpServer(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mcp-servers"] }),
  });

  const canSubmit = url.trim().length > 0;

  return (
    <div className="mx-auto w-full max-w-2xl px-6 pt-8 pb-12">
      <h1 className="text-lg font-semibold">Agent connections</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Connect remote MCP servers so the agent can use their tools on your
        behalf — their credentials are never exposed to the agent.
      </p>

      <section className="mt-6">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Add a server
        </h2>
        <form
          className="mt-3 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) add.mutate();
          }}
        >
          <div>
            <label className="block text-sm font-medium" htmlFor="mcp-url">
              Server URL
            </label>
            <input
              id="mcp-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com/mcp"
              className="mt-1 w-full rounded-md border border-border bg-background px-3 py-1.5 font-mono text-sm outline-none focus:border-ring"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Remote MCP endpoint (HTTPS). Streamable HTTP with SSE fallback.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium" htmlFor="mcp-bearer">
              Bearer token{" "}
              <span className="font-normal text-muted-foreground">
                (optional)
              </span>
            </label>
            <input
              id="mcp-bearer"
              type="password"
              value={bearer}
              onChange={(e) => setBearer(e.target.value)}
              placeholder="Leave blank to use OAuth"
              className="mt-1 w-full rounded-md border border-border bg-background px-3 py-1.5 font-mono text-sm outline-none focus:border-ring"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              For servers that authenticate with a static API key. Leave blank
              and we'll start an OAuth flow if the server requires it.
            </p>
          </div>
          {add.error ? (
            <p className="text-sm text-destructive">
              {(add.error as Error).message}
            </p>
          ) : null}
          <button
            type="submit"
            disabled={!canSubmit || add.isPending}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {add.isPending ? "Connecting…" : "Connect"}
          </button>
        </form>
      </section>

      <section className="mt-8">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Connected servers
        </h2>
        {servers.isLoading ? (
          <p className="mt-2 text-sm text-muted-foreground">Loading…</p>
        ) : servers.error ? (
          <p className="mt-2 text-sm text-destructive">
            {(servers.error as Error).message}
          </p>
        ) : (servers.data ?? []).length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">
            No servers connected yet.
          </p>
        ) : (
          <ul className="mt-2 divide-y divide-border rounded-md border border-border">
            {(servers.data ?? []).map((s) => (
              <li
                key={s.id}
                className="flex items-center justify-between gap-4 px-4 py-3"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{s.name}</span>
                    <code className="text-xs text-muted-foreground">
                      {s.id}
                    </code>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {s.description ? `${s.description} · ` : ""}
                    {s.toolCount} tool{s.toolCount === 1 ? "" : "s"}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <StatusChip state={s.state} />
                  <button
                    type="button"
                    onClick={() => remove.mutate(s.id)}
                    disabled={remove.isPending && remove.variables === s.id}
                    className="rounded-md border border-border px-2.5 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                  >
                    {remove.isPending && remove.variables === s.id
                      ? "Removing…"
                      : "Remove"}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
        {remove.error ? (
          <p className="mt-2 text-sm text-destructive">
            {(remove.error as Error).message}
          </p>
        ) : null}
      </section>
    </div>
  );
}
